import * as React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { USER_ROLES } from '@/constants/user-roles';
import { hasAnyAccountRoles, hasAnyRolesV2, hasCommunityRolesV2 } from '@/utils/has-roles';
import { hasAccess } from '@/utils/menu-roles-manager';
import { Avatar, Badge, Button, Divider } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowSquareOut as ArrowSquareOutIcon } from '@phosphor-icons/react/dist/ssr/ArrowSquareOut';
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight';

import type { Account } from '@/types/v2/account';
import { AccountType } from '@/types/v2/account';
import type { Community } from '@/types/v2/community';
import type { NavItemConfig } from '@/types/v2/nav';
import type { NavColor } from '@/types/v2/settings';
import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { isNavItemActive } from '@/lib/v2/is-nav-item-active';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { usePathname } from '@/hooks/v2/use-pathname';
import { usePopover } from '@/hooks/v2/use-popover';
import { useSettings } from '@/hooks/v2/use-settings';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { RouterLink } from '@/components/core/v2/link';
import { Logo } from '@/components/core/v2/logo';
import type { ColorScheme } from '@/styles/theme-v2/theme/types';
import { UserAvatar } from '@/components/common/user-avatar';

import { CommunitiesSwitch } from '../communities-switch';
import { icons } from '../nav-icons';
import { UserPopover } from '../user-popover/user-popover';
import { navColorStyles } from './styles';

const logoColors = {
  dark: { blend_in: 'light', discrete: 'light', evident: 'light' },
  light: { blend_in: 'dark', discrete: 'dark', evident: 'light' },
} as Record<ColorScheme, Record<NavColor, 'dark' | 'light'>>;

export interface SideNavProps {
  color?: NavColor;
  items?: NavItemConfig[];
  bottomItems?: NavItemConfig[];
  user?: User;
  community?: Community;
  account?: Account;
}

export function SideNav({
  color = 'evident',
  items = [],
  user,
  community,
  account,
  bottomItems = [],
}: SideNavProps): React.JSX.Element {
  const pathname = usePathname();
  const popover = usePopover<HTMLButtonElement>();
  const {
    settings: { colorScheme = 'light' },
  } = useSettings();
  const { isFeatureEnabled } = useFeatureFlags();

  const styles = navColorStyles[colorScheme][color];
  const logoColor = logoColors[colorScheme][color];
  return (
    <Box
      sx={{
        ...styles,
        bgcolor: 'var(--SideNav-background)',
        borderRight: 'var(--SideNav-border)',
        color: 'var(--SideNav-color)',
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        height: '100%',
        left: 0,
        position: 'fixed',
        top: 0,
        width: 'var(--SideNav-width)',
        zIndex: 'var(--SideNav-zIndex)',
      }}
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        <div>
          <Box component={RouterLink} href={paths.home} sx={{ display: 'inline-flex', textDecoration: 'none' }}>
            <Badge
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              badgeContent={
                <Chip
                  color="primary"
                  label="BETA"
                  size="small"
                  sx={{
                    height: 18,
                    borderRadius: 1,
                    ml: 1,
                    '& .MuiChip-label': {
                      px: 0.75,
                      lineHeight: '18px',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                    },
                  }}
                />
              }
              overlap="rectangular"
            >
              <Logo color={logoColor} height={50} />
            </Badge>
          </Box>
        </div>
        {account?.type === AccountType.MANAGED &&
        community &&
        user &&
        (hasCommunityRolesV2(community, [COMMUNITY_ROLES.COMMUNITY_MANAGER]) ||
          hasAnyAccountRoles(account, [ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER])) ? (
          <RoleGuard
            requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <CommunitiesSwitch />
          </RoleGuard>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              border: '1px solid var(--Workspaces-border-color)',
              borderRadius: '12px',
              px: 2,
              py: 1,
              boxShadow: (theme) => theme.shadows[1],
              background: (theme) => theme.palette.background.paper,
            }}
          >
            <Avatar src={community?.logo_url} variant="square">
              {community?.name
                .split(' ')
                .map((name: string) => name.charAt(0))
                .join('')
                .toUpperCase()}
            </Avatar>
            <Box sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
              <Typography color="var(--Workspaces-title-color)" variant="caption">
                Your Community
              </Typography>
              <Typography
                color="var(--Workspaces-name-color)"
                sx={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
                variant="subtitle2"
              >
                {community?.name}
              </Typography>
            </Box>
          </Stack>
        )}
      </Stack>
      <Box
        component="nav"
        sx={{
          flex: '1 1 auto',
          overflowY: 'auto',
          p: 2,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {renderNavGroups({ items, pathname, user, community, account, isFeatureEnabled })}
      </Box>
      <Box
        component="nav"
        sx={{
          overflowY: 'auto',
          p: 2,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {renderNavGroups({ items: bottomItems, pathname, user, community, account, isFeatureEnabled })}
      </Box>
      <Divider />
      <Box alignItems="center" display="flex" gap={1} sx={{ p: 2 }}>
        <Button
          color="inherit"
          onClick={popover.handleOpen}
          ref={popover.anchorRef}
          startIcon={<UserAvatar user={user} size="32px" />}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textTransform: 'none',
            padding: '10px 16px',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'var(--NavItem-hover-background)',
              color: 'var(--NavItem-hover-color)',
            },
          }}
          variant="contained"
        >
          <Stack alignItems="flex-start">
            <Typography variant="body2">
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="body2">{user?.email}</Typography>
          </Stack>
        </Button>
        <UserPopover anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} />
      </Box>
    </Box>
  );
}

function renderNavGroups({
  items,
  pathname,
  user,
  community,
  account,
  isFeatureEnabled,
}: {
  items: NavItemConfig[];
  pathname: string;
  user?: User;
  community?: Community;
  account?: Account;
  isFeatureEnabled: (flag: string) => boolean;
}): React.JSX.Element {
  const children = items.reduce((acc: React.ReactNode[], curr: NavItemConfig): React.ReactNode[] => {
    const { items: childItems, key, roles, communityRoles, accountRoles, ...item } = curr;
    const flags =
      typeof item.featureFlags === 'string'
        ? item.featureFlags.split(',')
        : Array.isArray(item.featureFlags)
          ? item.featureFlags
          : [];

    if (flags.length > 0) {
      const isEnabled = flags.some((flag) => isFeatureEnabled(flag));
      if (!isEnabled) return acc;
    }

    let itemHasAccess = true; // Default to true if no roles specified

    if (roles || accountRoles || communityRoles) {
      // Only check account roles if account with roles exists
      const itemHasAccountAccess =
        (roles || accountRoles) && account?.roles
          ? (roles || accountRoles)?.some((role) => account.roles?.includes(role as unknown as any))
          : false;
      const itemHasCommunityAccess =
        communityRoles && community?.roles
          ? communityRoles.some((role) => community.roles?.includes(role as unknown as any))
          : false;

      // If user has account with roles, check both account and community roles
      if (account?.roles && (roles || accountRoles)) {
        itemHasAccess = itemHasAccountAccess || itemHasCommunityAccess;
      } else {
        // If no account roles exist on user, only check community roles
        itemHasAccess = communityRoles ? itemHasCommunityAccess : true;
      }
    }

    if (!itemHasAccess) return acc; // Skip if user doesn't have access

    acc.push(
      <Stack component="li" key={curr.key} spacing={1.5}>
        {curr.title ? (
          <div>
            <Typography sx={{ color: 'var(--NavGroup-title-color)', fontSize: '0.875rem', fontWeight: 500 }}>
              {curr.title}
            </Typography>
          </div>
        ) : null}
        <div>
          {renderNavItems({ depth: 0, items: curr.items, pathname, user, community, account, isFeatureEnabled })}
        </div>
      </Stack>
    );

    return acc;
  }, []);

  return (
    <Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Stack>
  );
}

function renderNavItems({
  depth = 0,
  items = [],
  pathname,
  community,
  user,
  account,
  isFeatureEnabled,
}: {
  depth: number;
  items?: NavItemConfig[];
  pathname: string;
  user?: User;
  community?: Community;
  account?: Account;
  isFeatureEnabled: (flag: string) => boolean;
}): React.JSX.Element {
  const children = items.reduce((acc: React.ReactNode[], curr: NavItemConfig): React.ReactNode[] => {
    const { items: childItems, key, roles, communityRoles, accountRoles, ...item } = curr;
    const flags =
      typeof item.featureFlags === 'string'
        ? item.featureFlags.split(',')
        : Array.isArray(item.featureFlags)
          ? item.featureFlags
          : [];

    if (flags.length > 0) {
      const isEnabled = flags.some((flag) => isFeatureEnabled(flag));
      if (!isEnabled) return acc;
    }

    // Hide Discussion when community has discussion page disabled
    if (curr.key === 'discussion' && community?.discussion_page_enabled === false) return acc;

    let itemHasAccess = true; // Default to true if no roles specified

    if (roles || accountRoles || communityRoles) {
      // Only check account roles if account with roles exists
      const itemHasAccountAccess =
        (roles || accountRoles) && account?.roles
          ? (roles || accountRoles)?.some((role) => account.roles?.includes(role as unknown as any))
          : false;
      const itemHasCommunityAccess =
        communityRoles && community?.roles
          ? communityRoles.some((role) => community.roles?.includes(role as unknown as any))
          : false;

      // If user has account with roles, check both account and community roles
      if (account?.roles && (roles || accountRoles)) {
        itemHasAccess = itemHasAccountAccess || itemHasCommunityAccess;
      } else {
        // If no account roles exist on user, only check community roles
        itemHasAccess = communityRoles ? itemHasCommunityAccess : true;
      }
    }

    if (!itemHasAccess) return acc; // Skip if user doesn't have access

    const forceOpen = childItems
      ? Boolean(childItems.find((childItem) => childItem.href && pathname.startsWith(childItem.href)))
      : false;

    acc.push(
      <NavItem depth={depth} forceOpen={forceOpen} key={key} pathname={pathname} user={user} {...item}>
        {childItems ? renderNavItems({ depth: depth + 1, pathname, items: childItems, user, isFeatureEnabled }) : null}
      </NavItem>
    );

    return acc;
  }, []);

  return (
    <Stack component="ul" data-depth={depth} spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Stack>
  );
}

interface NavItemProps extends Omit<NavItemConfig, 'items'> {
  children?: React.ReactNode;
  depth: number;
  forceOpen?: boolean;
  pathname: string;
  user?: User;
  community?: Community;
  account?: Account;
}

function NavItem({
  children,
  depth,
  disabled,
  external,
  forceOpen = false,
  href,
  icon,
  label,
  matcher,
  pathname,
  title,
}: NavItemProps): React.JSX.Element {
  const [open, setOpen] = React.useState<boolean>(forceOpen);
  const active = isNavItemActive({
    disabled,
    external,
    href: href ?? '',
    matcher,
    pathname,
  });
  const Icon = icon ? icons[icon] : null;
  const ExpandIcon = open ? CaretDownIcon : CaretRightIcon;
  const isBranch = children && !href;
  const showChildren = Boolean(children && open);

  return (
    <Box component="li" data-depth={depth} sx={{ userSelect: 'none' }}>
      <Box
        {...(isBranch
          ? {
              onClick: (): void => {
                setOpen(!open);
              },
              onKeyUp: (event: React.KeyboardEvent<HTMLDivElement>): void => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setOpen(!open);
                }
              },
              role: 'button',
            }
          : {
              ...(href && !disabled
                ? {
                    component: external ? 'a' : RouterLink,
                    href,
                    target: external ? '_blank' : undefined,
                    rel: external ? 'noreferrer' : undefined,
                  }
                : { role: 'button' }),
            })}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          color: 'var(--NavItem-color)',
          cursor: 'pointer',
          display: 'flex',
          flex: '0 0 auto',
          gap: 1,
          p: '6px 16px',
          position: 'relative',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          ...(disabled && {
            bgcolor: 'var(--NavItem-disabled-background)',
            color: 'var(--NavItem-disabled-color)',
            cursor: 'not-allowed',
          }),
          ...(active && {
            bgcolor: 'var(--NavItem-active-background)',
            color: 'var(--NavItem-active-color)',
            ...(depth > 0 && {
              '&::before': {
                bgcolor: 'var(--NavItem-children-indicator)',
                borderRadius: '2px',
                content: '" "',
                height: '20px',
                left: '-14px',
                position: 'absolute',
                width: '3px',
              },
            }),
          }),
          ...(open && { color: 'var(--NavItem-open-color)' }),
          '&:hover': {
            ...(!disabled &&
              !active && {
                bgcolor: 'var(--NavItem-hover-background)',
                color: 'var(--NavItem-hover-color)',
              }),
          },
        }}
        tabIndex={0}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
          {Icon ? (
            <Icon
              fill={active ? 'var(--NavItem-icon-active-color)' : 'var(--NavItem-icon-color)'}
              fontSize="var(--icon-fontSize-md)"
              weight={forceOpen || active ? 'fill' : undefined}
            />
          ) : null}
        </Box>
        <Box sx={{ flex: '1 1 auto' }}>
          <Typography
            component="span"
            sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
          >
            {title}
          </Typography>
        </Box>
        {label ? <Chip color="primary" label={label} size="small" /> : null}
        {external ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ArrowSquareOutIcon color="var(--NavItem-icon-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
        {isBranch ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
      </Box>
      {showChildren ? (
        <Box sx={{ pl: '24px' }}>
          <Box sx={{ borderLeft: '1px solid var(--NavItem-children-border)', pl: '12px' }}>{children}</Box>
        </Box>
      ) : null}
    </Box>
  );
}
